import { useParams } from 'react-router-dom';
import { fetchAgentBrandList, fetchAgentList } from '../hooks/queries';
import { formatDate, safeLink, getActivePayment, getRecordDisplayStatus } from '../utils';
import { NUMERIC_DAYS_OF_WEEK } from '../constants';

import Loader from '../components/common/Loader';
import UrlPreview from '../components/common/UrlPreview';

const formatValue = (value?: any): string => {
  if (value === undefined || value === null || value === '') return '-';
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
};

const formatCurrencyField = (
  amount?: number,
  currency?: string,
  payType?: string
): string => {
  // if (!amount || !currency || !payType) return '-';
  return `${amount} ${currency} (${payType})`;
};

const formatNumberToOrdinal = (number: number): string => {
  return number === 1 ? '1st' : number === 2 ? '2nd' : number === 3 ? '3rd' : `${number}th`;
};

const formatBonus = (bonus?: { amount?: number; frequency?: string; dayOfWeek?: number; dayOfMonth?: number } | any[]) => {
  // bonus is now BonusRecord[] — pick the first active (no end date) record to display
  const b = Array.isArray(bonus) ? bonus.find((r) => !r.effectiveEndDate) ?? bonus[0] : bonus;
  if (!b?.amount || !b.frequency) return '-';
  if (b.frequency === 'specific_day_of_week')
    return b.dayOfWeek ? `Weekly on ${NUMERIC_DAYS_OF_WEEK.find((day) => day.value === b.dayOfWeek)?.label}` : '-';
  if (b.frequency === 'specific_day_of_month')
    return b.dayOfMonth ? `Monthly on ${formatNumberToOrdinal(b.dayOfMonth || 0)}` : '-';
  return `${b.amount} (${b.frequency})`;
};

const formatBreakDuration = (value?: number, unit?: string): string => {
  if (!value || !unit) return '-';
  return `${value} ${unit}`;
};

const SectionHeader = ({ title }: { title: string }) => (
  <h2 className="text-xl font-semibold border-b pb-2 text-gray-800">{title}</h2>
);

const Section = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <section className="space-y-4 bg-white p-6 rounded-lg shadow-sm">
    <SectionHeader title={title} />
    {children}
  </section>
);

const Field = ({ label, value }: { label: string; value?: any }) => (
  <div>
    <p className="text-xs text-gray-500">{label}</p>
    <p className="text-sm font-medium text-gray-800">
      {formatValue(value) || '-'}
    </p>
  </div>
);

const TimeSlotDisplay = ({
  timeSlots,
}: {
  timeSlots?: Record<string, any>;
}) => {
  if (!timeSlots || Object.keys(timeSlots).length === 0) {
    return <p className="text-sm text-gray-800">-</p>;
  }

  return (
    <div className="space-y-1">
      {Object.entries(timeSlots).map(([day, shifts]) => {
        if (!shifts || !Array.isArray(shifts) || shifts.length === 0) {
          return null;
        }

        return (
          <div key={day} className="space-y-1">
            {shifts.map((shift, index) => (
              <div
                key={`${day}-${index}`}
                className="flex justify-between text-sm p-2 bg-gray-100 rounded-md"
              >
                <span className="font-medium text-gray-700">
                  {day}
                  {shifts.length > 1 && (
                    <span className="text-xs text-gray-500 ml-1">
                      (Shift {index + 1})
                    </span>
                  )}
                </span>
                <span className="text-gray-600">
                  {`${shift.startTime} - ${shift.endTime} (${shift.workingHours} hrs)`}
                </span>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
};

const InfoGrid = ({ children }: { children: React.ReactNode }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">{children}</div>
);

const AgentView = () => {
  const { id } = useParams();
  const { data: agentData, isLoading } = fetchAgentList();
  const { data: agentBrandData, isLoading: isAgentBrandLoading } =
    fetchAgentBrandList();

  const agentStillWorks = (brand: any) => {
    return brand?.brandId?.agents?.includes(agent?.kimId);
  };

  const agent = agentData?.agents?.find((a) => a._id === id);
  const agentIdStr = agent?._id;
  const agentBrands = agentBrandData?.agentBrands?.filter(
    (brand) => brand.agentId === agentIdStr && agentStillWorks(brand)
  );

  const getBrandName = (ab: any) => ab?.brandId?.name;

  if (isLoading || isAgentBrandLoading) return <Loader />;

  if (!agent) {
    return (
      <p className="text-center text-gray-500 h-screen flex items-center justify-center">
        Agent not found
      </p>
    );
  }

  return (
    <div className="p-6 space-y-8 max-w-7xl mx-auto">
      <Section title="Personal & Contact Information">
        <InfoGrid>
          <Field label="Name" value={agent.name} />
          <Field label="Date of Birth" value={formatDate(agent?.dob || '-')} />
          <Field label="Personal Email" value={agent.personalEmail} />
          <Field label="Phone Number" value={agent.phoneNumber?.number} />
          <Field label="Whatsapp Number" value={agent.whatsappNumber?.number} />
          <UrlPreview label="Profile Picture" url={agent.profileUri} />
          <Field label="Government ID" value={agent.governmentId} />
          <UrlPreview label="Resume URL" url={agent.resumeUri} />
          <UrlPreview
            label="Application Video URL"
            url={agent.applicationVideoUri}
          />
          <Field label="Current Address" value={agent.currentAddress} />
        </InfoGrid>

        <div className="mt-6">
          <h3 className="text-lg font-medium text-gray-700">
            Emergency Contact
          </h3>
          <InfoGrid>
            <Field label="Name" value={agent.emergencyContact?.name} />
            <Field
              label="Phone Number"
              value={agent.emergencyContact?.phoneNumber}
            />
            <Field label="Permanent Address" value={agent?.permanentAddress} />
            <UrlPreview
              label="Job Application"
              url={`/jobs?email=${agent.personalEmail}`}
            />
          </InfoGrid>
        </div>
      </Section>

      <section className="space-y-4 bg-white p-6 rounded-lg shadow-sm">
        <SectionHeader title="Agent Brand Info" />

        <div className="grid grid-cols-2 gap-4 w-full">
          {agentBrands?.length ? (
            agentBrands.map((brand, index) => {
              const paymentRecords = Array.isArray(brand.payment)
                ? brand.payment
                : brand.payment
                  ? [brand.payment]
                  : [];

              return (
                <div
                  key={index}
                  className="bg-gray-50 p-4 rounded-md space-y-4 border"
                >
                  <h4 className="font-semibold text-gray-800">
                    {getBrandName(brand)}
                  </h4>
                  <InfoGrid>
                    <Field label="Email" value={brand.email} />
                    <Field
                      label="Onboarding Date"
                      value={formatDate(brand.onboardingDate)}
                    />
                    <Field
                      label="Offboarding Date"
                      value={formatDate(brand.offboardingDate)}
                    />
                    <Field
                      label="Status"
                      value={brand.isActive === false ? 'Inactive' : 'Active'}
                    />
                    <Field
                      label="Trial Status"
                      value={brand.trialPeriod?.status}
                    />
                    <Field
                      label="Trial Start Date"
                      value={formatDate(brand.trialPeriod?.startDate)}
                    />
                    <Field
                      label="Trial Duration"
                      value={formatValue(brand.trialPeriod?.days) + ' days'}
                    />
                    <Field label="Time Zone" value={brand.timezone} />
                    <Field
                      label="Capture Interval"
                      value={`${brand.captureTimeInterval || '-'} min`}
                    />
                    <Field
                      label="Break Duration"
                      value={formatBreakDuration(
                        brand.workSchedule?.breakDuration?.value,
                        brand.workSchedule?.breakDuration?.unit
                      )}
                    />
                    <Field label="Bonus" value={formatBonus(brand.bonus)} />
                  </InfoGrid>

                  {/* Payment History */}
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      Payment History
                    </p>
                    {paymentRecords.length > 0 ? (
                      <div className="space-y-2">
                        {paymentRecords.map((record, idx) => {
                          const status = getRecordDisplayStatus(record);
                          const isActive = status === 'active';
                          const isScheduled = status === 'scheduled';
                          return (
                            <div
                              key={idx}
                              className={`p-3 rounded border text-xs ${
                                isActive
                                  ? 'bg-green-50 border-green-200'
                                  : 'bg-white border-gray-200'
                              }`}
                            >
                              <div className="flex justify-between items-center mb-2">
                                <span className="font-medium text-gray-700">
                                  Record #{idx + 1}
                                </span>
                                {isActive && (
                                  <span className="px-2 py-0.5 text-xs bg-green-500 text-white rounded">
                                    Active
                                  </span>
                                )}
                                {isScheduled && (
                                  <span className="px-2 py-0.5 text-xs bg-amber-500 text-white rounded">
                                    Scheduled
                                  </span>
                                )}
                              </div>
                              <div className="grid grid-cols-2 gap-2 text-xs">
                                <div>
                                  <span className="text-gray-500">
                                    To Agent:
                                  </span>{' '}
                                  <span className="font-medium">
                                    {formatCurrencyField(
                                      record.toAgent?.amount,
                                      record.toAgent?.currency,
                                      record.toAgent?.payType
                                    )}
                                  </span>
                                </div>
                                <div>
                                  <span className="text-gray-500">
                                    From Client:
                                  </span>{' '}
                                  <span className="font-medium">
                                    {formatCurrencyField(
                                      record.fromClient?.amount,
                                      record.fromClient?.currency,
                                      record.fromClient?.payType
                                    )}
                                  </span>
                                </div>
                                <div className="col-span-2">
                                  <span className="text-gray-500">
                                    Effective:
                                  </span>{' '}
                                  <span className="font-medium">
                                    {formatDate(record.effectiveStartDate)} -{' '}
                                    {record.effectiveEndDate
                                      ? formatDate(record.effectiveEndDate)
                                      : 'Present'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-500">
                        No payment records
                      </p>
                    )}
                  </div>

                  {/* Work Schedule */}
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      Work Schedule
                    </p>
                    <TimeSlotDisplay
                      timeSlots={brand.workSchedule?.timeSlots}
                    />
                  </div>
                </div>
              );
            })
          ) : (
            <p className="text-sm text-gray-800">No brand assignments found.</p>
          )}
        </div>
      </section>

      <Section title="Professional Details">
        <InfoGrid>
          <Field label="Status" value={agent.status} />
          <Field label="Skills" value={agent.skills} />
          <Field label="Tools" value={agent.tools} />
          <Field label="Channels" value={agent.channels} />
        </InfoGrid>

        <div className="space-y-2">
          <h3 className="text-lg font-medium text-gray-700">Languages</h3>
          {agent.languages?.length ? (
            agent.languages.map((lang, i) => (
              <p key={i} className="text-sm text-gray-800">
                {lang.language} ({formatValue(lang.fluency)})
              </p>
            ))
          ) : (
            <p className="text-sm text-gray-800">-</p>
          )}
        </div>

        <div className="space-y-2">
          <h3 className="text-lg font-medium text-gray-700">Past Employers</h3>
          {agent.pastEmployers?.length ? (
            agent.pastEmployers.map((emp, i) => (
              <div
                key={i}
                className="border p-4 rounded-md space-y-2 bg-gray-50"
              >
                <Field label="Employer Name" value={emp.employerName} />
                <UrlPreview
                  label="Employer URL"
                  url={safeLink(emp.employerUrl)}
                />
                <Field label="Start Date" value={emp.startDate} />
                <Field label="End Date" value={emp.endDate} />
              </div>
            ))
          ) : (
            <p className="text-sm text-gray-800">-</p>
          )}
        </div>
      </Section>

      <Section title="Financial Details">
        <InfoGrid>
          <Field label="Bank Name" value={agent.bankDetails?.bankName} />
          <Field label="Name as per Bank" value={agent.bankDetails?.name} />
          <Field
            label="Account Number"
            value={agent.bankDetails?.accountNumber}
          />
          <Field label="Account Type" value={agent.bankDetails?.accountType} />
          <Field label="Bank Code" value={agent.bankDetails?.bankCode} />
          <Field label="Branch Name" value={agent.bankDetails?.branchName} />
          <Field label="City" value={agent.bankDetails?.city} />
        </InfoGrid>

        <InfoGrid>
          <Field label="PayPal ID" value={agent.paymentIds?.paypalId} />
          <Field label="Payoneer ID" value={agent.paymentIds?.payoneerId} />
          <Field label="Wise ID" value={agent.paymentIds?.wiseId} />
          <UrlPreview
            label="Bank Statement"
            url={agent.bankDetails?.bankStatement}
          />
        </InfoGrid>
      </Section>

      <Section title="Other Information">
        <Field label="Comments" value={agent.comments} />
      </Section>
    </div>
  );
};

export default AgentView;
